'use client';

import {
  forwardRef,
  useId,
  type ComponentPropsWithoutRef,
  type ElementRef,
  type ReactNode,
} from 'react';

import { Checkbox } from '../../primitives/checkbox';
import { cn } from '../../utils/cn';

type CheckboxBase = ComponentPropsWithoutRef<typeof Checkbox>;

export interface UzCheckboxProps extends CheckboxBase {
  label?: ReactNode;
  description?: ReactNode;
  error?: string;
  containerClassName?: string;
}

export const UzCheckbox = forwardRef<ElementRef<typeof Checkbox>, UzCheckboxProps>(
  ({ label, description, error, containerClassName, id: providedId, disabled, ...rest }, ref) => {
    const autoId = useId();
    const id = providedId ?? autoId;

    return (
      <div className={cn('flex flex-col gap-1', containerClassName)}>
        <div className="flex items-start gap-2">
          <Checkbox
            id={id}
            ref={ref}
            disabled={disabled}
            aria-invalid={!!error || undefined}
            {...rest}
          />
          {(label || description) && (
            <div className="grid gap-1 leading-none">
              {label && (
                <label
                  htmlFor={id}
                  className={cn(
                    'text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
                    disabled && 'cursor-not-allowed opacity-70',
                  )}
                >
                  {label}
                </label>
              )}
              {description && <p className="text-muted-foreground text-xs">{description}</p>}
            </div>
          )}
        </div>
        {error && <p className="text-destructive text-xs font-medium">{error}</p>}
      </div>
    );
  },
);
UzCheckbox.displayName = 'UzCheckbox';
